import { Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useKbFeatureFlags } from '@/queries/kb';
import type { KbArticle } from '@/types/kb';

type Restrictable = Pick<KbArticle, 'companyIds' | 'featureFlags' | 'companies'>;

export function isKbArticleRestricted(article: Restrictable): boolean {
  return Boolean(article.companyIds?.length || article.featureFlags?.length);
}

// `detailed` spells the restriction out beside the badge; elsewhere it sits in the title for
// pointers and in screen-reader text, since the badge can be inside a link where a tooltip
// trigger can't go.
export function KbRestrictedBadge({ article, className, detailed }: { article: Restrictable; className?: string; detailed?: boolean }) {
  const restricted = isKbArticleRestricted(article);
  const flags = useKbFeatureFlags({ enabled: Boolean(article.featureFlags?.length) });
  if (!restricted) return null;

  const companyIds = article.companyIds ?? [];
  const companyNames = companyIds.map(id => article.companies?.find(c => c._id === id)?.name || id);
  const featureNames = (article.featureFlags ?? []).map(code => flags.data?.find(f => f.code === code)?.label || code);
  const parts = [
    companyNames.length ? `Companies: ${companyNames.join(', ')}` : null,
    featureNames.length ? `Features (any of): ${featureNames.join(', ')}` : null
  ].filter((p): p is string => Boolean(p));

  return (
    <>
      <Badge variant="info" title={parts.join('\n')} className={className} data-testid="kb-restricted-badge">
        <Lock aria-hidden />
        Restricted
        {!detailed && <span className="sr-only">. {parts.join('. ')}</span>}
      </Badge>
      {detailed && <span data-testid="kb-restricted-detail">{parts.join('. ')}</span>}
    </>
  );
}
